import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, contactApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import '../styles/admin-dashboard.css';

function AdminContactPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [activeTab, setActiveTab] = useState('messages'); // 'messages' | 'settings'
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // Messages State
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, inProgress: 0, resolved: 0, archived: 0, unread: 0 });
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 15, pages: 1 });

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [readFilter, setReadFilter] = useState('all');

  // Selected messages for bulk action
  const [selectedIds, setSelectedIds] = useState([]);

  // Detail Modal
  const [viewingMessage, setViewingMessage] = useState(null);
  const [adminNoteInput, setAdminNoteInput] = useState('');
  const [updatingMessage, setUpdatingMessage] = useState(false);

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null, loading: false });
  const [bulkDeleteModal, setBulkDeleteModal] = useState({ isOpen: false, loading: false });

  // Reset Modal
  const [resetModal, setResetModal] = useState({ isOpen: false, loading: false });

  // Settings State
  const [settings, setSettings] = useState({
    badgeText: 'Get In Touch',
    heading: 'We’d Love to Hear from You',
    subheading: 'Have a question or need assistance? Reach out to our care team anytime.',
    phone: '+91 98765 43210',
    email: 'care@psychmitra.com',
    address: 'Bandra West, Mumbai, MH, India',
    workingHours: 'Mon – Sat: 9:00 AM – 8:00 PM',
    privacyNote: '100% Confidential & Secure',
    mapEmbedUrl:
      'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3771.441113038628!2d72.82583837584102!3d19.055325982144795!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c9410830616d%3A0x111b63353dbb1216!2sBandra%20West%2C%20Mumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin',
    mapDirectionsUrl: 'https://maps.google.com/?q=Bandra+West+Mumbai',
    isEnabled: true,
  });

  // Feedback toast
  const [feedback, setFeedback] = useState({ type: '', text: '' });
  const showFeedback = (type, text) => {
    setFeedback({ type, text });
    setTimeout(() => setFeedback({ type: '', text: '' }), 4000);
  };

  // Check Auth
  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
    }
  }, [authSession, navigate]);

  // Fetch Messages
  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const res = await contactApi.adminGetMessages({
        search: searchQuery,
        status: statusFilter,
        isRead: readFilter,
        page: pagination.page,
        limit: pagination.limit,
      });

      if (res?.success && res.data) {
        setMessages(res.data.messages || []);
        if (res.data.stats) setStats(res.data.stats);
        if (res.data.pagination) setPagination(res.data.pagination);
      }
    } catch (err) {
      console.error('Error fetching contact messages:', err);
      showFeedback('error', err.message || 'Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, readFilter, pagination.page, pagination.limit]);

  // Fetch Settings
  const fetchSettings = useCallback(async () => {
    try {
      const res = await contactApi.adminGetSettings();
      if (res?.success && res.data) {
        setSettings(res.data);
      }
    } catch (err) {
      console.error('Error loading contact settings:', err);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Handle View Single Message
  const handleOpenDetail = async (item) => {
    try {
      const res = await contactApi.adminGetMessageById(item._id);
      if (res?.success && res.data) {
        setViewingMessage(res.data);
        setAdminNoteInput(res.data.adminNotes || '');
        // Update read count locally
        setMessages((prev) =>
          prev.map((m) => (m._id === item._id ? { ...m, isRead: true } : m))
        );
        setStats((prev) => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
      }
    } catch (err) {
      showFeedback('error', err.message || 'Failed to load message details');
    }
  };

  // Update Status directly
  const handleStatusChange = async (id, newStatus) => {
    try {
      const res = await contactApi.adminUpdateMessage(id, { status: newStatus });
      if (res?.success && res.data) {
        setMessages((prev) =>
          prev.map((m) => (m._id === id ? { ...m, status: newStatus } : m))
        );
        if (viewingMessage && viewingMessage._id === id) {
          setViewingMessage((prev) => ({ ...prev, status: newStatus }));
        }
        showFeedback('success', `Inquiry status updated to ${newStatus}`);
      }
    } catch (err) {
      showFeedback('error', err.message || 'Failed to update status');
    }
  };

  // Save Admin Notes & Status in Modal
  const handleSaveModalUpdates = async (markReplied = false) => {
    if (!viewingMessage) return;
    try {
      setUpdatingMessage(true);
      const payload = {
        adminNotes: adminNoteInput,
        status: viewingMessage.status,
        markReplied,
      };

      const res = await contactApi.adminUpdateMessage(viewingMessage._id, payload);
      if (res?.success && res.data) {
        setViewingMessage(res.data);
        setMessages((prev) =>
          prev.map((m) => (m._id === viewingMessage._id ? res.data : m))
        );
        showFeedback('success', 'Inquiry notes saved successfully');
      }
    } catch (err) {
      showFeedback('error', err.message || 'Failed to update notes');
    } finally {
      setUpdatingMessage(false);
    }
  };

  // Delete Single Message
  const handleDeleteMessage = async () => {
    if (!deleteModal.item) return;
    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));
      const res = await contactApi.adminDeleteMessage(deleteModal.item._id);
      if (res?.success) {
        setMessages((prev) => prev.filter((m) => m._id !== deleteModal.item._id));
        showFeedback('success', 'Inquiry deleted successfully');
        setDeleteModal({ isOpen: false, item: null, loading: false });
        if (viewingMessage && viewingMessage._id === deleteModal.item._id) {
          setViewingMessage(null);
        }
      }
    } catch (err) {
      showFeedback('error', err.message || 'Failed to delete inquiry');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // Bulk Delete
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      setBulkDeleteModal({ isOpen: true, loading: true });
      const res = await contactApi.adminBulkDeleteMessages(selectedIds);
      if (res?.success) {
        setMessages((prev) => prev.filter((m) => !selectedIds.includes(m._id)));
        setSelectedIds([]);
        showFeedback('success', res.message || 'Messages deleted');
        setBulkDeleteModal({ isOpen: false, loading: false });
      }
    } catch (err) {
      showFeedback('error', err.message || 'Bulk delete failed');
      setBulkDeleteModal({ isOpen: false, loading: false });
    }
  };

  // Save Settings
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      setSavingSettings(true);
      const res = await contactApi.adminUpdateSettings(settings);
      if (res?.success && res.data) {
        setSettings(res.data);
        showFeedback('success', 'Contact Us page settings updated successfully!');
      }
    } catch (err) {
      showFeedback('error', err.message || 'Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Reset Settings
  const handleResetSettings = async () => {
    try {
      setResetModal((prev) => ({ ...prev, loading: true }));
      const res = await contactApi.adminResetSettings();
      if (res?.success && res.data) {
        setSettings(res.data);
        showFeedback('success', 'Contact settings reset to defaults');
        setResetModal({ isOpen: false, loading: false });
      }
    } catch (err) {
      showFeedback('error', err.message || 'Reset failed');
      setResetModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(messages.map((m) => m._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  return (
    <div className="admin-dashboard-container">
      <DashboardSidebar userRole="admin" />

      <div className="sidebar-content-wrapper admin-main-content">
        {/* Header */}
        <div className="admin-page-header">
          <div>
            <span className="admin-badge">📞 Communication</span>
            <h1 className="admin-title">Contact & Inquiries Manager</h1>
            <p className="admin-subtitle">
              Manage client inquiries, responses, helpline contact details, and map settings.
            </p>
          </div>
          <div className="admin-header-actions">
            <a
              href="/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="admin-preview-btn"
            >
              👁️ View Live Page
            </a>
          </div>
        </div>

        {/* Feedback Alert */}
        {feedback.text && (
          <div className={`admin-toast-feedback ${feedback.type}`}>
            <i
              className={`fas ${
                feedback.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'
              }`}
            ></i>
            <span>{feedback.text}</span>
          </div>
        )}

        {/* Stats Ribbon */}
        <div className="contact-stats-grid">
          <div className="stat-card">
            <div className="stat-icon-wrap total-stat">
              <i className="fas fa-inbox"></i>
            </div>
            <div>
              <span className="stat-num">{stats.total}</span>
              <span className="stat-title">Total Inquiries</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon-wrap unread-stat">
              <i className="fas fa-bell"></i>
            </div>
            <div>
              <span className="stat-num">{stats.unread}</span>
              <span className="stat-title">Unread Messages</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon-wrap new-stat">
              <i className="fas fa-star"></i>
            </div>
            <div>
              <span className="stat-num">{stats.new}</span>
              <span className="stat-title">New Inquiries</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon-wrap resolved-stat">
              <i className="fas fa-check-double"></i>
            </div>
            <div>
              <span className="stat-num">{stats.resolved}</span>
              <span className="stat-title">Resolved / Replied</span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="admin-tabs-bar">
          <button
            className={`admin-tab-btn ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
          >
            <i className="fas fa-envelope-open-text"></i> Received Inquiries ({stats.total})
          </button>
          <button
            className={`admin-tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <i className="fas fa-sliders-h"></i> Contact Page Settings & Info
          </button>
        </div>

        {/* TAB 1: INQUIRIES & MESSAGES CRUD */}
        {activeTab === 'messages' && (
          <div className="admin-tab-content">
            {/* Filter Bar */}
            <div className="messages-filter-card">
              <div className="row g-3 align-items-center">
                <div className="col-lg-5 col-md-6">
                  <div className="search-box-custom">
                    <i className="fas fa-search"></i>
                    <input
                      type="text"
                      placeholder="Search by name, email, phone, or message..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} className="clear-search-btn">
                        ×
                      </button>
                    )}
                  </div>
                </div>

                <div className="col-lg-3 col-md-3 col-6">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="admin-select-custom"
                  >
                    <option value="all">All Statuses</option>
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div className="col-lg-2 col-md-3 col-6">
                  <select
                    value={readFilter}
                    onChange={(e) => setReadFilter(e.target.value)}
                    className="admin-select-custom"
                  >
                    <option value="all">All Read States</option>
                    <option value="false">Unread Only</option>
                    <option value="true">Read Only</option>
                  </select>
                </div>

                <div className="col-lg-2 col-md-12 text-end">
                  {selectedIds.length > 0 && (
                    <button
                      onClick={() => setBulkDeleteModal({ isOpen: true, loading: false })}
                      className="bulk-delete-btn"
                    >
                      <i className="fas fa-trash-alt"></i> Delete ({selectedIds.length})
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Messages Table */}
            <div className="messages-table-card">
              {loading ? (
                <div className="table-loading-state">
                  <div className="spinner-border text-purple" role="status"></div>
                  <p>Loading inquiries...</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="table-empty-state">
                  <div className="empty-icon">📭</div>
                  <h4>No contact inquiries found</h4>
                  <p>No messages match your search or filter criteria.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="admin-custom-table">
                    <thead>
                      <tr>
                        <th width="40">
                          <input
                            type="checkbox"
                            checked={
                              messages.length > 0 && selectedIds.length === messages.length
                            }
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>Sender</th>
                        <th>Contact Details</th>
                        <th>Message Excerpt</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th width="120" className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {messages.map((item) => (
                        <tr
                          key={item._id}
                          className={`${!item.isRead ? 'unread-row' : ''}`}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(item._id)}
                              onChange={() => handleToggleSelect(item._id)}
                            />
                          </td>
                          <td>
                            <div className="sender-cell">
                              <span className="sender-name">{item.name}</span>
                              {!item.isRead && <span className="unread-dot">NEW</span>}
                            </div>
                          </td>
                          <td>
                            <div className="contact-cell">
                              <a href={`mailto:${item.email}`} className="cell-link">
                                <i className="fas fa-envelope"></i> {item.email}
                              </a>
                              {item.phone && (
                                <a href={`tel:${item.phone}`} className="cell-link">
                                  <i className="fas fa-phone-alt"></i> {item.phone}
                                </a>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="message-excerpt-cell">
                              <span className="msg-preview">{item.message}</span>
                              {item.adminNotes && (
                                <span className="admin-note-badge" title={item.adminNotes}>
                                  📝 Note
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <select
                              value={item.status}
                              onChange={(e) => handleStatusChange(item._id, e.target.value)}
                              className={`status-pill status-${item.status}`}
                            >
                              <option value="new">New</option>
                              <option value="in_progress">In Progress</option>
                              <option value="resolved">Resolved</option>
                              <option value="archived">Archived</option>
                            </select>
                          </td>
                          <td>
                            <span className="date-cell">
                              {new Date(item.createdAt).toLocaleDateString('en-IN', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          </td>
                          <td className="text-end">
                            <div className="table-action-btns">
                              <button
                                onClick={() => handleOpenDetail(item)}
                                className="action-btn view-btn"
                                title="View Message Details"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                              <button
                                onClick={() =>
                                  setDeleteModal({ isOpen: true, item, loading: false })
                                }
                                className="action-btn delete-btn"
                                title="Delete Inquiry"
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: CONTACT PAGE CONTENT & SETTINGS */}
        {activeTab === 'settings' && (
          <div className="admin-tab-content">
            <div className="settings-form-card">
              <div className="settings-header">
                <div>
                  <h3>Contact Page Information & Location</h3>
                  <p>Update official helpline numbers, email, physical office address, and Google Maps embed.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setResetModal({ isOpen: true, loading: false })}
                  className="reset-defaults-btn"
                >
                  <i className="fas fa-undo"></i> Reset Defaults
                </button>
              </div>

              <form onSubmit={handleSaveSettings}>
                <div className="row g-4">
                  {/* Badge Text */}
                  <div className="col-md-4">
                    <label className="form-label-admin">Header Badge Text</label>
                    <input
                      type="text"
                      className="form-control-admin"
                      value={settings.badgeText}
                      onChange={(e) =>
                        setSettings({ ...settings, badgeText: e.target.value })
                      }
                      placeholder="e.g. Get In Touch"
                    />
                  </div>

                  {/* Main Heading */}
                  <div className="col-md-8">
                    <label className="form-label-admin">Main Page Heading</label>
                    <input
                      type="text"
                      className="form-control-admin"
                      value={settings.heading}
                      onChange={(e) =>
                        setSettings({ ...settings, heading: e.target.value })
                      }
                      placeholder="e.g. We’d Love to Hear from You"
                    />
                  </div>

                  {/* Subtitle */}
                  <div className="col-12">
                    <label className="form-label-admin">Subheading / Description</label>
                    <input
                      type="text"
                      className="form-control-admin"
                      value={settings.subheading}
                      onChange={(e) =>
                        setSettings({ ...settings, subheading: e.target.value })
                      }
                      placeholder="e.g. Have a question or need assistance? Reach out anytime."
                    />
                  </div>

                  {/* Phone */}
                  <div className="col-md-6">
                    <label className="form-label-admin">Phone Helpline Number</label>
                    <input
                      type="text"
                      className="form-control-admin"
                      value={settings.phone}
                      onChange={(e) =>
                        setSettings({ ...settings, phone: e.target.value })
                      }
                      placeholder="e.g. +91 98765 43210"
                    />
                  </div>

                  {/* Email */}
                  <div className="col-md-6">
                    <label className="form-label-admin">Official Email Address</label>
                    <input
                      type="email"
                      className="form-control-admin"
                      value={settings.email}
                      onChange={(e) =>
                        setSettings({ ...settings, email: e.target.value })
                      }
                      placeholder="e.g. care@psychmitra.com"
                    />
                  </div>

                  {/* Address */}
                  <div className="col-md-6">
                    <label className="form-label-admin">Office / Centre Address</label>
                    <input
                      type="text"
                      className="form-control-admin"
                      value={settings.address}
                      onChange={(e) =>
                        setSettings({ ...settings, address: e.target.value })
                      }
                      placeholder="e.g. Bandra West, Mumbai, MH, India"
                    />
                  </div>

                  {/* Working Hours */}
                  <div className="col-md-6">
                    <label className="form-label-admin">Working Hours</label>
                    <input
                      type="text"
                      className="form-control-admin"
                      value={settings.workingHours}
                      onChange={(e) =>
                        setSettings({ ...settings, workingHours: e.target.value })
                      }
                      placeholder="e.g. Mon – Sat: 9:00 AM – 8:00 PM"
                    />
                  </div>

                  {/* Google Maps Embed URL */}
                  <div className="col-12">
                    <label className="form-label-admin">
                      Google Maps Embed URL (iframe src)
                    </label>
                    <input
                      type="text"
                      className="form-control-admin"
                      value={settings.mapEmbedUrl}
                      onChange={(e) =>
                        setSettings({ ...settings, mapEmbedUrl: e.target.value })
                      }
                      placeholder="https://www.google.com/maps/embed?..."
                    />
                    <small className="form-hint">
                      Paste the iframe src URL from Google Maps Embed feature.
                    </small>
                  </div>

                  {/* Google Maps Directions URL */}
                  <div className="col-12">
                    <label className="form-label-admin">
                      Google Maps Directions Link
                    </label>
                    <input
                      type="text"
                      className="form-control-admin"
                      value={settings.mapDirectionsUrl}
                      onChange={(e) =>
                        setSettings({ ...settings, mapDirectionsUrl: e.target.value })
                      }
                      placeholder="https://maps.google.com/?q=..."
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="col-12 mt-4">
                    <button
                      type="submit"
                      disabled={savingSettings}
                      className="admin-save-btn"
                    >
                      {savingSettings ? 'Saving Settings...' : '💾 Save Contact Settings'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* DETAIL MODAL */}
      {viewingMessage && (
        <div className="admin-modal-overlay" onClick={() => setViewingMessage(null)}>
          <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <h4>Inquiry Details</h4>
              <button
                onClick={() => setViewingMessage(null)}
                className="modal-close-btn"
              >
                ×
              </button>
            </div>

            <div className="modal-body-custom">
              <div className="inquiry-profile-bar">
                <div className="profile-avatar-circle">
                  {viewingMessage.name ? viewingMessage.name.charAt(0).toUpperCase() : 'U'}
                </div>
                <div>
                  <h5>{viewingMessage.name}</h5>
                  <div className="contact-links-row">
                    <a href={`mailto:${viewingMessage.email}`}>
                      <i className="fas fa-envelope"></i> {viewingMessage.email}
                    </a>
                    {viewingMessage.phone && (
                      <a href={`tel:${viewingMessage.phone}`}>
                        <i className="fas fa-phone-alt"></i> {viewingMessage.phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>

              <div className="inquiry-message-card">
                <label className="inquiry-label">Message Content:</label>
                <div className="inquiry-text-block">{viewingMessage.message}</div>
                <div className="inquiry-meta">
                  Received on:{' '}
                  {new Date(viewingMessage.createdAt).toLocaleString('en-IN', {
                    dateStyle: 'full',
                    timeStyle: 'short',
                  })}
                </div>
              </div>

              {/* Status Selector */}
              <div className="inquiry-status-group">
                <label className="inquiry-label">Current Status:</label>
                <select
                  value={viewingMessage.status}
                  onChange={(e) =>
                    setViewingMessage({ ...viewingMessage, status: e.target.value })
                  }
                  className="admin-select-custom"
                >
                  <option value="new">New</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved / Replied</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              {/* Admin Notes */}
              <div className="inquiry-notes-group">
                <label className="inquiry-label">Admin Follow-up Notes:</label>
                <textarea
                  rows="3"
                  className="form-control-admin"
                  placeholder="Add internal notes about conversation, assigned therapist, or action taken..."
                  value={adminNoteInput}
                  onChange={(e) => setAdminNoteInput(e.target.value)}
                ></textarea>
              </div>
            </div>

            <div className="modal-footer-custom">
              <a
                href={`mailto:${viewingMessage.email}?subject=Regarding your PsychMitra inquiry`}
                className="reply-email-btn"
              >
                <i className="fas fa-reply"></i> Reply via Email
              </a>
              <button
                type="button"
                disabled={updatingMessage}
                onClick={() => handleSaveModalUpdates(false)}
                className="admin-save-btn"
              >
                {updatingMessage ? 'Saving...' : 'Save Updates'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteModal.isOpen && (
        <div
          className="admin-modal-overlay"
          onClick={() => setDeleteModal({ isOpen: false, item: null, loading: false })}
        >
          <div className="admin-modal-box small-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <h4>Confirm Deletion</h4>
              <button
                onClick={() =>
                  setDeleteModal({ isOpen: false, item: null, loading: false })
                }
                className="modal-close-btn"
              >
                ×
              </button>
            </div>
            <div className="modal-body-custom text-center py-4">
              <p>
                Are you sure you want to delete inquiry from{' '}
                <strong>{deleteModal.item?.name}</strong>?
              </p>
              <small className="text-muted">This action cannot be undone.</small>
            </div>
            <div className="modal-footer-custom justify-content-center">
              <button
                className="btn-cancel"
                onClick={() =>
                  setDeleteModal({ isOpen: false, item: null, loading: false })
                }
              >
                Cancel
              </button>
              <button
                className="btn-confirm-delete"
                disabled={deleteModal.loading}
                onClick={handleDeleteMessage}
              >
                {deleteModal.loading ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BULK DELETE CONFIRMATION MODAL */}
      {bulkDeleteModal.isOpen && (
        <div
          className="admin-modal-overlay"
          onClick={() => setBulkDeleteModal({ isOpen: false, loading: false })}
        >
          <div className="admin-modal-box small-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <h4>Delete Multiple Inquiries</h4>
              <button
                onClick={() => setBulkDeleteModal({ isOpen: false, loading: false })}
                className="modal-close-btn"
              >
                ×
              </button>
            </div>
            <div className="modal-body-custom text-center py-4">
              <p>
                Are you sure you want to delete{' '}
                <strong>{selectedIds.length} selected inquiries</strong>?
              </p>
            </div>
            <div className="modal-footer-custom justify-content-center">
              <button
                className="btn-cancel"
                onClick={() => setBulkDeleteModal({ isOpen: false, loading: false })}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-delete"
                disabled={bulkDeleteModal.loading}
                onClick={handleBulkDelete}
              >
                {bulkDeleteModal.loading ? 'Deleting...' : 'Delete Selected'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RESET CONFIRMATION MODAL */}
      {resetModal.isOpen && (
        <div
          className="admin-modal-overlay"
          onClick={() => setResetModal({ isOpen: false, loading: false })}
        >
          <div className="admin-modal-box small-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <h4>Reset Contact Settings</h4>
              <button
                onClick={() => setResetModal({ isOpen: false, loading: false })}
                className="modal-close-btn"
              >
                ×
              </button>
            </div>
            <div className="modal-body-custom text-center py-4">
              <p>
                Reset all contact phone numbers, email, address, and map embeds to default PsychMitra templates?
              </p>
            </div>
            <div className="modal-footer-custom justify-content-center">
              <button
                className="btn-cancel"
                onClick={() => setResetModal({ isOpen: false, loading: false })}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-delete"
                disabled={resetModal.loading}
                onClick={handleResetSettings}
              >
                {resetModal.loading ? 'Resetting...' : 'Yes, Reset Defaults'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-main-content {
          padding: 30px 35px 60px;
          background: #f8f6fc;
          min-height: 100vh;
        }

        .admin-page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 28px;
        }

        .admin-badge {
          display: inline-block;
          background: #ede3fa;
          color: #7c4bb9;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 4px 14px;
          border-radius: 20px;
          margin-bottom: 6px;
        }

        .admin-title {
          font-size: 28px;
          font-weight: 800;
          color: #21123e;
          margin: 0 0 4px 0;
        }

        .admin-subtitle {
          font-size: 14px;
          color: #6a627a;
          margin: 0;
        }

        .admin-preview-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #ffffff;
          border: 1.5px solid #dcd1ee;
          color: #7c4bb9;
          font-size: 13.5px;
          font-weight: 700;
          padding: 10px 18px;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.25s ease;
        }

        .admin-preview-btn:hover {
          background: #7c4bb9;
          color: #ffffff;
        }

        /* Stats Grid */
        .contact-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 25px;
        }

        .stat-card {
          background: #ffffff;
          border-radius: 18px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          border: 1px solid #ede4fa;
          box-shadow: 0 4px 15px rgba(124, 75, 185, 0.04);
        }

        .stat-icon-wrap {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }

        .total-stat { background: #ede3fa; color: #7c4bb9; }
        .unread-stat { background: #fef2f2; color: #dc2626; }
        .new-stat { background: #fef3c7; color: #d97706; }
        .resolved-stat { background: #dcfce7; color: #16a34a; }

        .stat-num {
          display: block;
          font-size: 24px;
          font-weight: 800;
          color: #21123e;
          line-height: 1;
        }

        .stat-title {
          font-size: 12.5px;
          color: #79718a;
          margin-top: 4px;
          display: block;
        }

        /* Tabs */
        .admin-tabs-bar {
          display: flex;
          gap: 10px;
          border-bottom: 2px solid #e7ddf6;
          margin-bottom: 22px;
        }

        .admin-tab-btn {
          background: none;
          border: none;
          padding: 12px 20px;
          font-size: 14.5px;
          font-weight: 700;
          color: #6a627a;
          cursor: pointer;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .admin-tab-btn.active {
          color: #7c4bb9;
          border-bottom-color: #7c4bb9;
        }

        /* Filter Card */
        .messages-filter-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 16px 20px;
          border: 1px solid #ede4fa;
          margin-bottom: 18px;
        }

        .search-box-custom {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-box-custom i {
          position: absolute;
          left: 14px;
          color: #8c82a2;
        }

        .search-box-custom input {
          width: 100%;
          height: 42px;
          padding: 0 35px 0 38px;
          background: #faf8fd;
          border: 1.5px solid #e7ddf6;
          border-radius: 10px;
          font-size: 13.5px;
          color: #21123e;
          outline: none;
        }

        .clear-search-btn {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          font-size: 18px;
          color: #8c82a2;
          cursor: pointer;
        }

        .admin-select-custom {
          width: 100%;
          height: 42px;
          padding: 0 14px;
          background: #faf8fd;
          border: 1.5px solid #e7ddf6;
          border-radius: 10px;
          font-size: 13.5px;
          color: #21123e;
          outline: none;
        }

        .bulk-delete-btn {
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #fca5a5;
          padding: 9px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
        }

        /* Table */
        .messages-table-card {
          background: #ffffff;
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid #ede4fa;
          box-shadow: 0 4px 15px rgba(124, 75, 185, 0.04);
        }

        .admin-custom-table {
          width: 100%;
          border-collapse: collapse;
        }

        .admin-custom-table th {
          background: #faf8fd;
          padding: 14px 18px;
          font-size: 12.5px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #6a627a;
          border-bottom: 1.5px solid #ede4fa;
        }

        .admin-custom-table td {
          padding: 14px 18px;
          border-bottom: 1px solid #f3effa;
          font-size: 13.5px;
          vertical-align: middle;
        }

        .admin-custom-table tr.unread-row {
          background: #fdfaff;
          font-weight: 600;
        }

        .sender-cell {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .sender-name {
          font-weight: 700;
          color: #21123e;
        }

        .unread-dot {
          background: #dc2626;
          color: #ffffff;
          font-size: 10px;
          font-weight: 800;
          padding: 2px 6px;
          border-radius: 10px;
        }

        .contact-cell {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .cell-link {
          color: #6a627a;
          text-decoration: none;
          font-size: 12.5px;
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }

        .cell-link:hover {
          color: #7c4bb9;
        }

        .message-excerpt-cell {
          max-width: 320px;
        }

        .msg-preview {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          color: #4a425a;
          font-size: 13px;
        }

        .admin-note-badge {
          display: inline-block;
          background: #fef3c7;
          color: #92400e;
          font-size: 10.5px;
          font-weight: 700;
          padding: 2px 6px;
          border-radius: 6px;
          margin-top: 4px;
        }

        /* Status Pills */
        .status-pill {
          padding: 4px 10px;
          border-radius: 14px;
          font-size: 12px;
          font-weight: 700;
          border: 1px solid transparent;
          cursor: pointer;
          outline: none;
        }

        .status-new { background: #fef3c7; color: #92400e; border-color: #fde68a; }
        .status-in_progress { background: #e0e7ff; color: #3730a3; border-color: #c7d2fe; }
        .status-resolved { background: #dcfce7; color: #166534; border-color: #bbf7d0; }
        .status-archived { background: #f3f4f6; color: #4b5563; border-color: #e5e7eb; }

        .date-cell {
          font-size: 12.5px;
          color: #8c82a2;
          white-space: nowrap;
        }

        .table-action-btns {
          display: inline-flex;
          gap: 6px;
        }

        .action-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 13px;
          transition: all 0.2s ease;
        }

        .view-btn { background: #ede3fa; color: #7c4bb9; }
        .view-btn:hover { background: #7c4bb9; color: #ffffff; }

        .delete-btn { background: #fee2e2; color: #dc2626; }
        .delete-btn:hover { background: #dc2626; color: #ffffff; }

        /* Settings Form */
        .settings-form-card {
          background: #ffffff;
          border-radius: 18px;
          padding: 32px;
          border: 1px solid #ede4fa;
        }

        .settings-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 24px;
          padding-bottom: 18px;
          border-bottom: 1px solid #f3effa;
        }

        .settings-header h3 {
          font-size: 18px;
          font-weight: 800;
          color: #21123e;
          margin: 0 0 4px 0;
        }

        .settings-header p {
          font-size: 13.5px;
          color: #6a627a;
          margin: 0;
        }

        .reset-defaults-btn {
          background: #fdf2f2;
          color: #b91c1c;
          border: 1px solid #fecaca;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
        }

        .form-label-admin {
          font-size: 13px;
          font-weight: 700;
          color: #382a50;
          margin-bottom: 6px;
          display: block;
        }

        .form-control-admin {
          width: 100%;
          height: 44px;
          padding: 0 16px;
          background: #faf8fd;
          border: 1.5px solid #e7ddf6;
          border-radius: 10px;
          font-size: 14px;
          color: #21123e;
          outline: none;
        }

        .form-control-admin:focus {
          border-color: #7c4bb9;
          background: #ffffff;
        }

        textarea.form-control-admin {
          height: auto;
          padding: 12px 16px;
        }

        .form-hint {
          font-size: 12px;
          color: #8c82a2;
          margin-top: 4px;
          display: block;
        }

        .admin-save-btn {
          background: #7c4bb9;
          color: #ffffff;
          border: none;
          padding: 12px 28px;
          border-radius: 12px;
          font-size: 14.5px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s;
        }

        .admin-save-btn:hover:not(:disabled) {
          background: #6a3aa8;
        }

        /* Modal */
        .admin-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(18, 9, 36, 0.6);
          backdrop-filter: blur(4px);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .admin-modal-box {
          background: #ffffff;
          border-radius: 20px;
          width: 100%;
          max-width: 620px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
        }

        .admin-modal-box.small-modal {
          max-width: 440px;
        }

        .modal-header-custom {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid #f0eaf8;
        }

        .modal-header-custom h4 {
          font-size: 18px;
          font-weight: 800;
          color: #21123e;
          margin: 0;
        }

        .modal-close-btn {
          background: none;
          border: none;
          font-size: 24px;
          color: #8c82a2;
          cursor: pointer;
        }

        .modal-body-custom {
          padding: 24px;
          max-height: 75vh;
          overflow-y: auto;
        }

        .inquiry-profile-bar {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid #f3effa;
        }

        .profile-avatar-circle {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #ede3fa;
          color: #7c4bb9;
          font-size: 20px;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .inquiry-profile-bar h5 {
          font-size: 17px;
          font-weight: 800;
          color: #21123e;
          margin: 0 0 4px 0;
        }

        .contact-links-row {
          display: flex;
          gap: 14px;
        }

        .contact-links-row a {
          font-size: 13px;
          color: #7c4bb9;
          text-decoration: none;
        }

        .inquiry-message-card {
          background: #faf8fd;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 18px;
          border: 1px solid #ede4fa;
        }

        .inquiry-label {
          font-size: 12.5px;
          font-weight: 700;
          color: #6a627a;
          margin-bottom: 6px;
          display: block;
        }

        .inquiry-text-block {
          font-size: 14px;
          color: #21123e;
          line-height: 1.6;
          white-space: pre-wrap;
          margin-bottom: 10px;
        }

        .inquiry-meta {
          font-size: 11.5px;
          color: #8c82a2;
        }

        .inquiry-status-group, .inquiry-notes-group {
          margin-bottom: 18px;
        }

        .modal-footer-custom {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 24px;
          background: #faf8fd;
          border-top: 1px solid #f0eaf8;
        }

        .reply-email-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #ffffff;
          border: 1.5px solid #dcd1ee;
          color: #7c4bb9;
          font-size: 13.5px;
          font-weight: 700;
          padding: 10px 18px;
          border-radius: 10px;
          text-decoration: none;
        }

        .btn-cancel {
          background: #f3f4f6;
          color: #374151;
          border: none;
          padding: 10px 20px;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
        }

        .btn-confirm-delete {
          background: #dc2626;
          color: #ffffff;
          border: none;
          padding: 10px 20px;
          border-radius: 10px;
          font-weight: 700;
          cursor: pointer;
        }

        .admin-toast-feedback {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 18px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 18px;
        }

        .admin-toast-feedback.success {
          background: #ecfdf5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }

        .admin-toast-feedback.error {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .table-empty-state, .table-loading-state {
          padding: 50px 20px;
          text-align: center;
        }

        .empty-icon {
          font-size: 44px;
          margin-bottom: 10px;
        }
      `}</style>
    </div>
  );
}

export default AdminContactPage;
